import React, { useRef, useState } from 'react';
import { motion, useInView } from 'framer-motion';
import { ArrowRightIcon, CheckIcon } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { useEscaCopy } from '../../i18n/escaCopy';
import { track } from '../../utils/analytics';
const EASE = [0.23, 1, 0.32, 1] as const;
const ENDPOINT = 'https://formspree.io/f/mdalrzke';
interface Fields {
  name: string;
  company: string;
  contact: string;
  role: string;
}
type Errors = Partial<Record<keyof Fields, string>>;
const EMPTY: Fields = {
  name: '',
  company: '',
  contact: '',
  role: ''
};
function getUtm(): Record<string, string> {
  if (typeof window === 'undefined') return {};
  const params = new URLSearchParams(window.location.search);
  const out: Record<string, string> = {};
  for (const key of ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content']) {
    const v = params.get(key);
    if (v) out[key] = v;
  }
  return out;
}
function isValidContact(value: string): boolean {
  const v = value.trim();
  if (v.includes('@')) return /^\S+@\S+\.\S{2,}$/.test(v);
  return v.replace(/\D/g, '').length >= 9;
}
export function DiscussSection() {
  const {
    lang
  } = useLanguage();
  const c = useEscaCopy(lang);
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  const [values, setValues] = useState<Fields>(EMPTY);
  const [errors, setErrors] = useState<Errors>({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [failed, setFailed] = useState(false);
  const startedRef = useRef(false);
  const change = (key: keyof Fields) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (!startedRef.current) {
      startedRef.current = true;
      track('form_start');
    }
    setValues(p => ({
      ...p,
      [key]: e.target.value
    }));
    setErrors(p => p[key] ? {
      ...p,
      [key]: undefined
    } : p);
  };
  const validate = (): boolean => {
    const next: Errors = {};
    if (!values.name.trim()) next.name = c.contact.errors.name;
    if (!values.contact.trim()) next.contact = c.contact.errors.contact;else if (!isValidContact(values.contact)) next.contact = c.contact.errors.contactInvalid;
    if (!values.role.trim()) next.role = c.contact.errors.role;
    setErrors(next);
    return Object.keys(next).length === 0;
  };
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFailed(false);
    if (!validate()) return;
    setSubmitting(true);
    try {
      const res = await fetch(ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          name: values.name,
          company: values.company,
          contact: values.contact,
          looking_for: values.role,
          source: 'esca_discuss',
          ...getUtm()
        })
      });
      if (!res.ok) throw new Error('request failed');
      track('form_submit', {
        source: 'esca_discuss'
      });
      setDone(true);
      setValues(EMPTY);
    } catch {
      setFailed(true);
    } finally {
      setSubmitting(false);
    }
  };
  const inputCls = (invalid: boolean) => `w-full px-4 py-3.5 bg-ink-light border text-sand placeholder-muted text-[1.0625rem] transition-colors duration-200 focus:outline-none focus:border-accent ${invalid ? 'border-accent' : 'border-line-dark'}`;
  return <section id="discuss" className="bg-ink text-sand py-20 lg:py-28" ref={ref} data-id="element-240" data-mp="3r2uqf41j8tw6">
      <div className="max-w-[88rem] mx-auto px-5 sm:px-8" data-id="element-241" data-mp="3w0wk0iwl4blw">
        <div className="grid lg:grid-cols-[minmax(0,30rem)_1fr] gap-12 lg:gap-24" data-id="element-242" data-mp="3w3iqef7qol2j">
          <motion.div initial={{
          opacity: 0,
          y: 16
        }} animate={inView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.55,
          ease: EASE
        }} data-id="element-243" data-mp="4w66hsfh1tix">
            <h2 className="esca-h2 text-sand text-balance" data-id="element-244" data-mp="1g8729rar6i86">
              {c.contact.title}
            </h2>
            <p className="esca-lead mt-7 text-muted-light max-w-[46ch]" data-id="element-245" data-mp="1dxfz4xlne9eh">
              {c.contact.lead}
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 16
        }} animate={inView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.55,
          delay: 0.1,
          ease: EASE
        }} className="lg:max-w-[36rem] w-full" data-id="element-246" data-mp="1nu2kdnhvz2h1">
            {done ? <div className="border border-line-dark bg-ink-light p-8 lg:p-10" data-id="element-247" data-mp="1ssm6pp0e6g9x">
                <div className="inline-flex items-center justify-center w-11 h-11 bg-accent" data-id="element-248" data-mp="1sqjh3635ip5q">
                  <CheckIcon className="w-5 h-5 text-white" data-id="element-249" data-mp="26m527269kosv" />
                </div>
                <h3 className="mt-6 font-display text-2xl font-bold text-sand tracking-[-0.025em]" data-id="element-250" data-mp="21lhd4m1lii1a">
                  {c.contact.successTitle}
                </h3>
                <p className="mt-3 text-[1.0625rem] text-muted-light leading-relaxed max-w-[46ch]" data-id="element-251" data-mp="2bjwtbtz346w0">
                  {c.contact.successText}
                </p>
                <button type="button" onClick={() => {
              setDone(false);
              startedRef.current = false;
            }} className="mt-7 text-[0.9375rem] font-semibold text-accent hover:text-accent-600 transition-colors duration-200" data-id="element-252" data-mp="2v32snn9wemvb">
                  {c.contact.successReset}
                </button>
              </div> : <form onSubmit={submit} noValidate className="space-y-6" data-id="element-253" data-mp="2g8ofi3d0m7ok">
                <div data-id="element-254" data-mp="22gi0wlpig8r8">
                  <label htmlFor="esca-name" className="block text-[0.875rem] font-medium text-muted-light mb-2" data-id="element-255" data-mp="22haz2yaelpxr">
                    {c.contact.nameLabel}
                  </label>
                  <input id="esca-name" type="text" autoComplete="name" value={values.name} onChange={change('name')} placeholder={c.contact.namePlaceholder} className={inputCls(!!errors.name)} data-id="element-256" data-mp="1sk57zq1shyxj" />
                  {errors.name && <p className="mt-2 text-[0.8125rem] text-accent" data-id="element-257" data-mp="14n07jqoccqba">
                      {errors.name}
                    </p>}
                </div>

                <div data-id="element-258" data-mp="1xacsjweusq2h">
                  <label htmlFor="esca-company" className="block text-[0.875rem] font-medium text-muted-light mb-2" data-id="element-259" data-mp="1xammfv30rhdg">
                    {c.contact.companyLabel}
                  </label>
                  <input id="esca-company" type="text" autoComplete="organization" value={values.company} onChange={change('company')} placeholder={c.contact.companyPlaceholder} className={inputCls(false)} data-id="element-260" data-mp="1ien5duxxm6w6" />
                </div>

                <div data-id="element-261" data-mp="2nlep33fqzfrc">
                  <label htmlFor="esca-contact" className="block text-[0.875rem] font-medium text-muted-light mb-2" data-id="element-262" data-mp="2nkn38kbycwe9">
                    {c.contact.contactLabel}
                  </label>
                  <input id="esca-contact" type="text" inputMode="text" value={values.contact} onChange={change('contact')} placeholder={c.contact.contactPlaceholder} className={inputCls(!!errors.contact)} data-id="element-263" data-mp="2xdaxb7bjsm9a" />
                  {errors.contact && <p className="mt-2 text-[0.8125rem] text-accent" data-id="element-264" data-mp="3g7zlwjezsk2a">
                      {errors.contact}
                    </p>}
                </div>

                <div data-id="element-265" data-mp="1lwkwy4tt2onl">
                  <label htmlFor="esca-role" className="block text-[0.875rem] font-medium text-muted-light mb-2" data-id="element-266" data-mp="1ly5k39dsj5bf">
                    {c.contact.roleLabel}
                  </label>
                  <textarea id="esca-role" rows={3} value={values.role} onChange={change('role')} placeholder={c.contact.rolePlaceholder} className={`${inputCls(!!errors.role)} resize-none`} data-id="element-267" data-mp="1ea3njelji86" />
                  {errors.role && <p className="mt-2 text-[0.8125rem] text-accent" data-id="element-268" data-mp="3eswzlw7ryus1">
                      {errors.role}
                    </p>}
                </div>

                {failed && <p className="text-[0.875rem] text-accent" data-id="element-269" data-mp="u2o383lc2mlu">
                    {c.contact.errorGeneric}
                  </p>}

                <button type="submit" disabled={submitting} className="group inline-flex w-full items-center justify-center gap-2.5 px-8 py-4 text-base font-semibold text-white bg-accent hover:bg-accent-600 transition-colors duration-200 disabled:opacity-60 disabled:cursor-not-allowed" data-id="element-270" data-mp="p2tbjxtqhszq">
                  {submitting ? <>
                      <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-271" data-mp="roaw55ww6lot" />
                      {c.contact.submitting}
                    </> : <>
                      {c.contact.submit}
                      <ArrowRightIcon className="w-4 h-4 transition-transform duration-200 ease-out group-hover:translate-x-1" data-id="element-272" data-mp="16k8p6fbir9wt" />
                    </>}
                </button>

                <p className="text-[0.8125rem] text-muted leading-relaxed" data-id="element-273" data-mp="1ybeqv06w1fo7">
                  {c.contact.disclaimer}
                </p>
              </form>}
          </motion.div>
        </div>
      </div>
    </section>;
}